import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { HttpService } from '../../http.service';
import FuzzySearch from 'fuzzy-search';

@Injectable({ providedIn: 'root' })
export class RosterMemberService {

  constructor(private httpService: HttpService, private http: HttpClient) {}

  // ── Pure transformations ──────────────────────────────────────────────────

  sortByKey(arr: any[], key: string, order: 'asc' | 'desc' = 'asc'): any[] {
    return [...arr].sort((a, b) => {
      if (a[key] === undefined || b[key] === undefined) return 0;
      if (typeof a[key] === 'string' && typeof b[key] === 'string') {
        return order === 'asc' ? a[key].localeCompare(b[key]) : b[key].localeCompare(a[key]);
      }
      return order === 'asc'
        ? (a[key] > b[key] ? 1 : a[key] < b[key] ? -1 : 0)
        : (a[key] < b[key] ? 1 : a[key] > b[key] ? -1 : 0);
    });
  }

  fuzzySearchMembers(members: any[], term: string): any[] {
    const searchParams = ['email', 'first_name', 'last_name'];
    let resultMembers = [...members];
    let resultArr: any[] = [];
    const searchFunc = (type: string) => {
      const searcher = new FuzzySearch(resultMembers, [type], { caseSensitive: false });
      resultArr = searcher.search(term);
      resultMembers = resultMembers.filter(
        (item1) => !resultArr.some((item2: any) => item1.id === item2.id)
      );
    };
    searchParams.forEach((item) => {
      if (resultMembers?.length > 0) {
        searchFunc(item === 'email' ? 'default_identity.' + item : item);
      }
    });
    return resultArr;
  }

  // ── API: Fetch members ────────────────────────────────────────────────────

  fetchMembers(orgId: string, perPage: number, page: number, queryType: string, sortType: string): Observable<any> {
    return this.httpService.getRequest(
      `${environment.profileURL}/api/v1/organizations/${orgId}/members.json?per_page=${perPage}&page=${page}&${queryType}=${sortType}`
    ) as Observable<any>;
  }

  sortMembers(orgId: string, perPage: number, queryType: string, sortType: string): Observable<any> {
    return this.httpService.getRequest(
      `${environment.profileURL}/api/v1/organizations/${orgId}/members.json?per_page=${perPage}&${queryType}=${sortType}`
    ) as Observable<any>;
  }

  searchMembers(orgId: string, perPage: number, searchby: string, value: string): Observable<any> {
    return this.httpService.getRequest(
      `${environment.profileURL}/api/v1/organizations/${orgId}/members.json?per_page=${perPage}&${searchby}=${value}`
    ) as Observable<any>;
  }

  fetchInvitationMembers(orgId: string, invitationId: string, count: number): Observable<any> {
    return this.httpService.getRequest(
      `${environment.profileURL}/api/v1/organizations/${orgId}/members.json?invitation_ids[]=${invitationId}&per_page=${count}`
    ) as Observable<any>;
  }

  fetchTeamMemberships(teamId: string, perPage: number, page: number = 1): Observable<any> {
    return this.httpService.getRequest(
      `${environment.profileURL}/api/v1/teams/${teamId}/memberships.json?first_name_order=asc&per_page=${perPage}&page=${page}`
    ) as Observable<any>;
  }

  // ── API: Member mutations ─────────────────────────────────────────────────

  addNewMember(teamId: string, email: string): Observable<any> {
    return from(this.httpService.postRequest(
      `${environment.profileURL}/api/v1/teams/${teamId}/add_members.json`,
      { members_attributes: { '0': { email, role_id: 3 } } }
    ) as Promise<Observable<any>>).pipe(switchMap(obs => obs));
  }

  addMembersToTeam(teamId: string, membersAttributes: any): Observable<any> {
    return from(this.httpService.postRequest(
      `${environment.profileURL}/api/v1/teams/${teamId}/add_members.json`,
      { members_attributes: membersAttributes }
    ) as Promise<Observable<any>>).pipe(switchMap(obs => obs));
  }

  addMemberToTeamByIdentity(identityId: string, teamId: string, roleId: number = 3): Observable<any> {
    return from(this.httpService.postRequest(
      `${environment.profileURL}/api/v1/identities/${identityId}/memberships.json`,
      { team_id: teamId, membership_role_id: roleId }
    ) as Promise<Observable<any>>).pipe(switchMap(obs => obs));
  }

  removeMemberFromTeam(identityId: string, membershipId: string): Observable<any> {
    return this.http.delete(
      `${environment.profileURL}/api/v1/identities/${identityId}/memberships/${membershipId}.json`
    );
  }

  removeMemberFromTeamByTeam(teamId: string, membershipId: string): Observable<any> {
    return this.http.delete(
      `${environment.profileURL}/api/v1/teams/${teamId}/memberships/${membershipId}.json`
    );
  }

  updateMemberRole(identityId: string, membershipId: string, roleId: number): Observable<any> {
    return from(this.httpService.putRequest(
      `${environment.profileURL}/api/v1/identities/${identityId}/memberships/${membershipId}.json`,
      { membership_role_id: roleId }
    ) as Promise<Observable<any>>).pipe(switchMap(obs => obs));
  }
}
